import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Hero3D from './Hero3D';
import MagneticButton from './MagneticButton';

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
};

function FeatureCard({ step, title, description, icon, index }) {
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isHovering, setIsHovering] = useState(false);

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setMousePosition({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <motion.div 
            initial={{ opacity: 0, y: 80, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.8, delay: index * 0.2, ease: [0.22, 1, 0.36, 1] }}
            
            /* --- THE HOVER POP ANIMATION --- */
            whileHover={{ 
                y: -10, 
                scale: 1.03,
                borderColor: 'rgba(255, 255, 255, 0.3)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.6), 0 0 20px rgba(255,255,255,0.05)',
                transition: { duration: 0.3, ease: "easeOut" }
            }}
            
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            className="card" 
            style={{ 
                padding: '40px', display: 'flex', flexDirection: 'column', gap: '16px', 
                position: 'relative', overflow: 'hidden', 
                backgroundColor: 'rgba(10,10,10,0.4)', backdropFilter: 'blur(10px)',
                border: '1px solid var(--border)',
                cursor: 'pointer'
            }}
        >
            {/* Spotlight Effect */}
            {isHovering && (
                <div style={{
                    position: 'absolute',
                    top: mousePosition.y - 100,
                    left: mousePosition.x - 100,
                    width: '200px',
                    height: '200px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    pointerEvents: 'none',
                    zIndex: 0,
                }} />
            )}

            <div style={{ position: 'absolute', top: '-10px', right: '10px', fontSize: '120px', fontWeight: '700', color: 'rgba(255,255,255,0.03)', pointerEvents: 'none', zIndex: 0 }}>
                {step}
            </div>
            
            <div style={{ fontSize: '32px', marginBottom: '8px', position: 'relative', zIndex: 1 }}>{icon}</div>
            <h3 style={{ fontSize: '24px', color: 'var(--text)', margin: 0, position: 'relative', zIndex: 1 }}>{title}</h3>
            <p style={{ fontSize: '15px', color: 'var(--text-secondary)', lineHeight: '1.6', margin: 0, position: 'relative', zIndex: 1 }}>
                {description}
            </p>
        </motion.div>
    );
}

function Landing() {
    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end start"]
    });
    
    const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
    const heroY = useTransform(scrollYProgress, [0, 0.5], [0, 100]);

    const marqueeText = "Join the marketplace. Hire talent. Earn money. ".repeat(10);

    return (
        <div className="landing-page" style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden', backgroundColor: 'var(--bg)' }}>
            
            <Hero3D />

            {/* Navbar */}
            <nav style={{ padding: '24px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 10 }}>
                <h1 style={{ fontSize: '24px', fontWeight: '600', letterSpacing: '-0.04em', margin: 0, color: 'var(--text)' }}>infloir</h1>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    {/* FIXED LOG IN BUTTON */}
                    <Link to="/login" style={{ 
                        color: 'var(--text)', 
                        fontSize: '14px', 
                        fontWeight: '500',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid var(--border)',
                        background: 'rgba(255, 255, 255, 0.03)',
                        transition: 'all 0.2s ease',
                        textDecoration: 'none'
                    }}>
                        Log in
                    </Link>
                    <Link to="/register" className="btn-primary" style={{ padding: '8px 16px', fontSize: '14px' }}>Get Started</Link>
                </div>
            </nav>

            {/* Hero Section */}
            <motion.main 
                ref={containerRef}
                style={{ opacity: heroOpacity, y: heroY, position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '80px 20px 40px' }}
            >
                <motion.div variants={staggerContainer} initial="hidden" animate="visible">
                    <motion.div variants={fadeUp} style={{ marginBottom: '24px' }}>
                        <span className="badge">✨ The Global Task Marketplace</span>
                    </motion.div>

                    <motion.h1 variants={fadeUp} style={{ fontSize: '72px', lineHeight: '1.1', fontWeight: '600', letterSpacing: '-0.04em', maxWidth: '900px', margin: '0 auto 24px', textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}>
                        The operating system for <br />
                        <span style={{ color: 'var(--text-muted)' }}>managed tasks.</span>
                    </motion.h1>

                    <motion.p variants={fadeUp} style={{ fontSize: '18px', color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto 48px', lineHeight: '1.6', textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
                        Businesses launch campaigns. Workers complete tasks. INFLOIR handles the escrow, verification, and payouts seamlessly.
                    </motion.p>

                    {/* Buttons with Magnetic Effect */}
                    <motion.div variants={fadeUp} style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                        <MagneticButton>
                            <Link to="/register?role=BUSINESS" className="btn-primary" style={{ 
                                padding: '16px 32px', fontSize: '16px', display: 'inline-block',
                                boxShadow: '0 4px 30px rgba(255, 255, 255, 0.2)'
                            }}>
                                Hire Talent
                            </Link>
                        </MagneticButton>
                        
                        <MagneticButton>
                            <Link to="/register?role=WORKER" className="btn-secondary" style={{ 
                                padding: '16px 32px', fontSize: '16px', display: 'inline-block',
                                background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(12px)', 
                                WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(255, 255, 255, 0.2)',
                                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)'
                            }}>
                                Start Earning
                            </Link>
                        </MagneticButton>
                    </motion.div>
                </motion.div>
            </motion.main>

            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '200px', background: 'linear-gradient(to bottom, transparent, var(--bg))', zIndex: 5, pointerEvents: 'none' }} />

            {/* --- INFINITE MARQUEE --- */}
            <div style={{ borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '24px 0', overflow: 'hidden', position: 'relative', zIndex: 10, backgroundColor: 'var(--bg)' }}>
                <motion.div
                    animate={{ x: [0, -1000] }}
                    transition={{ repeat: Infinity, ease: "linear", duration: 20 }}
                    style={{ display: 'flex', whiteSpace: 'nowrap', gap: '48px', color: 'var(--text-muted)', fontSize: '14px', fontWeight: '500', letterSpacing: '0.1em', textTransform: 'uppercase' }}
                >
                    <span>{marqueeText}</span>
                    <span>{marqueeText}</span>
                </motion.div>
            </div>

            {/* --- HOW IT WORKS SECTION --- */}
            <section style={{ position: 'relative', zIndex: 10, padding: '100px 48px', maxWidth: '1200px', margin: '0 auto' }}>
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    style={{ textAlign: 'center', marginBottom: '64px' }}
                >
                    <h2 style={{ fontSize: '40px', fontWeight: '600', letterSpacing: '-0.03em', marginBottom: '16px', color: 'var(--text)' }}>
                        How INFLOIR works.
                    </h2>
                    <p style={{ fontSize: '18px', color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                        Three steps to frictionless task management. No middlemen, no delays.
                    </p>
                </motion.div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                    <FeatureCard index={0} step="01" icon="🚀" title="Launch a Campaign" description="Businesses deposit funds into secure escrow. Our AI helps generate the perfect task briefs and target audience." />
                    <FeatureCard index={1} step="02" icon="⚡" title="Workers Execute" description="Verified workers browse the marketplace, accept tasks, and submit proof of completion instantly." />
                    <FeatureCard index={2} step="03" icon="💸" title="Instant Payouts" description="Once approved, escrow releases automatically. Workers get paid instantly to their wallet, businesses get results." />
                </div>
            </section>

            {/* --- VALUE PROPOSITION --- */}
            <section style={{ padding: '40px 48px 100px', maxWidth: '1200px', margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 10 }}>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.2em', marginBottom: '24px' }}>
                    Built for the future of work
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '48px', flexWrap: 'wrap', opacity: 0.6 }}>
                    <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', letterSpacing: '-0.02em' }}>Enterprise-Grade Security</span>
                    <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', letterSpacing: '-0.02em' }}>Instant Payouts</span>
                    <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', letterSpacing: '-0.02em' }}>Global Reach</span>
                    <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', letterSpacing: '-0.02em' }}>Verified Talent</span>
                </div>
            </section>

            {/* --- FOOTER --- */}
            <footer style={{ borderTop: '1px solid var(--border)', padding: '48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '24px', position: 'relative', zIndex: 10 }}>
                <div>
                    <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text)', margin: 0, letterSpacing: '-0.04em' }}>infloir</h2>
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>© {new Date().getFullYear()} INFLOIR. All rights reserved.</p>
                </div>
                <div style={{ display: 'flex', gap: '24px', fontSize: '14px' }}>
                    <Link to="/terms" style={{ color: 'var(--text-secondary)' }}>Terms</Link>
                    <Link to="/privacy" style={{ color: 'var(--text-secondary)' }}>Privacy</Link>
                    <Link to="/contact" style={{ color: 'var(--text-secondary)' }}>Contact</Link>
                </div>
            </footer>

        </div>
    );
}

export default Landing;